import {
  MARKETPLACE_EXPERIENCE_MONTH_BAND_LOWER_BOUNDS,
  MARKETPLACE_SALARY_LPA_BAND_LOWER_BOUNDS,
  MARKETPLACE_NOTICE_DAY_BAND_LOWER_BOUNDS,
  MARKETPLACE_RECENCY_DAY_BAND_LOWER_BOUNDS,
  marketplaceExperienceYearsRangeToBandRange,
  marketplaceSalaryRangeToBandRange,
  marketplaceNoticeDaysToBandIndex,
  marketplaceRecencyDaysToBandIndex,
  type MarketplaceSearchSpecV2,
} from '@vhire/shared/service-types/marketplace-search';

type Range = { min?: number; max?: number };
type SalaryField = MarketplaceSearchSpecV2['filters']['salaryField'];
export type MarketplacePrecisionKind = 'experience' | 'salary' | 'notice' | 'activity';

/** Describe the enforced field, including the expected-first fallback rule. */
export function salaryFieldLabel(field: SalaryField = 'either'): string {
  return field === 'current' ? 'Current salary' : field === 'expected' ? 'Expected salary'
    : 'Expected salary (current if missing)';
}

/** One presentation for inputs, applied chips and evidence. Match the serving
 * lattice, including the entire bucket containing an inclusive requested max. */
export function marketplacePrecisionLabel(kind: 'experience' | 'salary', value: Range | undefined, salaryField?: SalaryField): string | null;
export function marketplacePrecisionLabel(kind: 'notice' | 'activity', value: number | undefined): string | null;
export function marketplacePrecisionLabel(kind: MarketplacePrecisionKind, value: Range | number | undefined, salaryField?: SalaryField): string | null {
  if (value === undefined) return null;
  // Day constraints are upper-bound scalars, never experience/salary ranges.
  // Invalid runtime inputs must not look like an enforced open-ended day band.
  let bands: { min?: number; max?: number } | undefined;
  if (kind === 'notice' || kind === 'activity') {
    if (typeof value !== 'number') return null;
    const max = kind === 'notice' ? marketplaceNoticeDaysToBandIndex(value) : marketplaceRecencyDaysToBandIndex(value);
    if (max === null) return null;
    bands = { max };
  } else {
    if (typeof value !== 'object' || value === null) return null;
    if (value.min === undefined && value.max === undefined) return null;
    bands = kind === 'experience' ? marketplaceExperienceYearsRangeToBandRange(value) : marketplaceSalaryRangeToBandRange(value);
  }
  const bounds = kind === 'experience' ? MARKETPLACE_EXPERIENCE_MONTH_BAND_LOWER_BOUNDS
    : kind === 'salary' ? MARKETPLACE_SALARY_LPA_BAND_LOWER_BOUNDS
      : kind === 'notice' ? MARKETPLACE_NOTICE_DAY_BAND_LOWER_BOUNDS : MARKETPLACE_RECENCY_DAY_BAND_LOWER_BOUNDS;
  const divisor = kind === 'experience' ? 12 : 1;
  const min = bounds[bands?.min ?? 0]! / divisor;
  const upper = bands?.max === undefined ? undefined : bounds[bands.max + 1];
  const unit = kind === 'experience' ? 'years' : kind === 'salary' ? 'LPA' : 'days';
  const window = upper === undefined ? `${min}+ ${unit}`
    : `${min}–under ${upper / divisor} ${unit}`;
  const name = { experience: 'Experience', salary: salaryFieldLabel(salaryField), notice: 'Notice', activity: 'Activity' }[kind];
  return `${name}: ${window} (privacy bands)`;
}

/** Five-second server budget plus bounded network and render slack. Search
 * callers use this; reveal/debit and unrelated requests retain their own budget. */
export const RECRUITER_SEARCH_BROWSER_TIMEOUT_MS = 6_500;
